import { kebabCase } from 'es-toolkit';

import { _id, _postTitles } from 'src/_mock/assets';

// ----------------------------------------------------------------------

const MOCK_ID = _id[1];
const MOCK_TITLE = _postTitles[2];

const ROOTS = {
  AUTH: '/auth',
  AUTH_DEMO: '/auth-demo',
  ADMIN: '/admin',
};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/kontakt',
  betingelser: '/betingelser',
  faqs: '/faqs',
  page403: '/error/403',
  page404: '/error/404',
  page500: '/error/500',
  thankyou: '/thank-you',
  components: '/components',
  docs: 'https://docs.minimals.cc/',
  changelog: 'https://docs.minimals.cc/changelog/',
  zoneStore: 'https://mui.com/store/items/zone-landing-page/',
  minimalStore: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figmaUrl: 'https://www.figma.com/design/oAxS3CUFO0ou7rH2lTS8oI/%5BPreview%5D-Minimal-Web.v7.0.0',
  order: (id: string) => `/order/${id}`,
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id: string) => `/product/${id}`,
    demo: { details: `/product/${MOCK_ID}` },
  },
  post: {
    root: `/post`,
    details: (title: string) => `/post/${kebabCase(title)}`,
    demo: { details: `/post/${kebabCase(MOCK_TITLE)}` },
  },
  // AUTH
  auth: {
    amplify: {
      signIn: `${ROOTS.AUTH}/amplify/sign-in`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      signUp: `${ROOTS.AUTH}/amplify/sign-up`,
      updatePassword: `${ROOTS.AUTH}/amplify/update-password`,
      resetPassword: `${ROOTS.AUTH}/amplify/reset-password`,
    },
    jwt: {
      signIn: `${ROOTS.AUTH}/jwt/sign-in`,
      signUp: `${ROOTS.AUTH}/jwt/sign-up`,
    },
    firebase: {
      signIn: `${ROOTS.AUTH}/firebase/sign-in`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      signUp: `${ROOTS.AUTH}/firebase/sign-up`,
      resetPassword: `${ROOTS.AUTH}/firebase/reset-password`,
    },
    auth0: { signIn: `${ROOTS.AUTH}/auth0/sign-in` },
    supabase: {
      signIn: `${ROOTS.AUTH}/supabase/sign-in`,
      verify: `${ROOTS.AUTH}/supabase/verify`,
      signUp: `${ROOTS.AUTH}/supabase/sign-up`,
      updatePassword: `${ROOTS.AUTH}/supabase/update-password`,
      resetPassword: `${ROOTS.AUTH}/supabase/reset-password`,
    },
  },
  authDemo: {
    split: {
      signIn: `${ROOTS.AUTH_DEMO}/split/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/split/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/split/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/split/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/split/verify`,
    },
    centered: {
      signIn: `${ROOTS.AUTH_DEMO}/centered/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/centered/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/centered/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/centered/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/centered/verify`,
    },
  },
  // DASHBOARD
  admin: {
    root: ROOTS.ADMIN,
    mail: `${ROOTS.ADMIN}/mail`,
    chat: `${ROOTS.ADMIN}/chat`,
    blank: `${ROOTS.ADMIN}/blank`,
    kanban: `${ROOTS.ADMIN}/kanban`,
    calendar: `${ROOTS.ADMIN}/calendar`,
    fileManager: `${ROOTS.ADMIN}/file-manager`,
    permission: `${ROOTS.ADMIN}/permission`,
    general: {
      app: `${ROOTS.ADMIN}/app`,
      ecommerce: `${ROOTS.ADMIN}/ecommerce`,
      analytics: `${ROOTS.ADMIN}/analytics`,
      banking: `${ROOTS.ADMIN}/banking`,
      booking: `${ROOTS.ADMIN}/booking`,
      file: `${ROOTS.ADMIN}/file`,
      course: `${ROOTS.ADMIN}/course`,
    },
    user: {
      root: `${ROOTS.ADMIN}/user`,
      new: `${ROOTS.ADMIN}/user/new`,
      list: `${ROOTS.ADMIN}/user/list`,
      cards: `${ROOTS.ADMIN}/user/cards`,
      profile: `${ROOTS.ADMIN}/user/profile`,
      account: `${ROOTS.ADMIN}/user/account`,
      edit: (id: string) => `${ROOTS.ADMIN}/user/${id}/edit`,
      demo: { edit: `${ROOTS.ADMIN}/user/${MOCK_ID}/edit` },
    },
    product: {
      root: `${ROOTS.ADMIN}/product`,
      new: `${ROOTS.ADMIN}/product/new`,
      details: (id: string) => `${ROOTS.ADMIN}/product/${id}`,
      edit: (id: string) => `${ROOTS.ADMIN}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.ADMIN}/product/${MOCK_ID}`,
        edit: `${ROOTS.ADMIN}/product/${MOCK_ID}/edit`,
      },
    },
    invoice: {
      root: `${ROOTS.ADMIN}/invoice`,
      new: `${ROOTS.ADMIN}/invoice/new`,
      details: (id: string) => `${ROOTS.ADMIN}/invoice/${id}`,
      edit: (id: string) => `${ROOTS.ADMIN}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.ADMIN}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.ADMIN}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.ADMIN}/post`,
      new: `${ROOTS.ADMIN}/post/new`,
      details: (title: string) => `${ROOTS.ADMIN}/post/${kebabCase(title)}`,
      edit: (title: string) => `${ROOTS.ADMIN}/post/${kebabCase(title)}/edit`,
      demo: {
        details: `${ROOTS.ADMIN}/post/${kebabCase(MOCK_TITLE)}`,
        edit: `${ROOTS.ADMIN}/post/${kebabCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.ADMIN}/order`,
      details: (id: string) => `${ROOTS.ADMIN}/order/${id}`,
      new: `${ROOTS.ADMIN}/order/new`,
      edit: (id: string) => `${ROOTS.ADMIN}/order/${id}/edit`,
    },
    expense: {
      root: `${ROOTS.ADMIN}/expense`,
      new: `${ROOTS.ADMIN}/expense/new`,
      edit: (id: string) => `${ROOTS.ADMIN}/expense/${id}/edit`,
    },
    job: {
      root: `${ROOTS.ADMIN}/job`,
      new: `${ROOTS.ADMIN}/job/new`,
      details: (id: string) => `${ROOTS.ADMIN}/job/${id}`,
      edit: (id: string) => `${ROOTS.ADMIN}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.ADMIN}/job/${MOCK_ID}`,
        edit: `${ROOTS.ADMIN}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.ADMIN}/tour`,
      new: `${ROOTS.ADMIN}/tour/new`,
      details: (id: string) => `${ROOTS.ADMIN}/tour/${id}`,
      edit: (id: string) => `${ROOTS.ADMIN}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.ADMIN}/tour/${MOCK_ID}`,
        edit: `${ROOTS.ADMIN}/tour/${MOCK_ID}/edit`,
      },
    },
  },
};
